// The stored `audit_events` row as the domain reads it — the live/replay mode it
// was written under, the row shape itself, and the one invariant that pairs a
// tool-call kind with a `tool` name.
// Split out of `../audit.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { AuditEventIdSchema, SessionIdSchema } from '../../value-objects/ids';
import { AuditEventKind, AuditEventKindSchema } from './kind';

export const AuditMode = {
  Live: 'live',
  Replay: 'replay',
} as const;
export const AuditModeSchema = z.enum(AuditMode);
export type AuditMode = z.infer<typeof AuditModeSchema>;

export const AuditEventSchema = z.object({
  id: AuditEventIdSchema,
  sessionId: SessionIdSchema,
  /**
   * The human actor behind this event — the `users.id` of the dashboard/API
   * principal that triggered it, threaded from `Principal.userId` (slice 02).
   * Null for an autonomous bot loop (the agent acting on its own), which is the
   * common case: a null actor is the truthful record that no human stood behind
   * the action. Stored verbatim (never rewritten) so the trail attributes the
   * act even after the user offboards.
   */
  userId: z.string().nullable(),
  ts: z.date(),
  kind: AuditEventKindSchema,
  tool: z.string().min(1).nullable(),
  input: z.record(z.string(), z.unknown()).nullable(),
  output: z.record(z.string(), z.unknown()).nullable(),
  reasoning: z.string().nullable(),
  visibleToMaintainer: z.boolean(),
  /** Fields removed before insert; documents *why* something is absent. */
  redaction: z.array(z.string()),
  /** sha256 of the previous row's canonical JSON. Tamper-evident chain. */
  prevHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'expected sha256 hex')
    .nullable(),
  mode: AuditModeSchema,
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

/** Tool-call kinds that should carry a `tool` name. */
export const TOOL_CALL_KINDS: ReadonlySet<AuditEventKind> = new Set([
  AuditEventKind.ToolCallPending,
  AuditEventKind.ToolCallOk,
  AuditEventKind.ToolCallErr,
  AuditEventKind.McpToolCallPending,
  AuditEventKind.McpToolCallOk,
  AuditEventKind.McpToolCallErr,
]);

/**
 * Invariant: tool-call rows MUST have `tool` set. Non-tool rows MUST NOT.
 */
export function toolFieldConsistent(event: AuditEvent): boolean {
  const isToolCall = TOOL_CALL_KINDS.has(event.kind);
  return isToolCall ? event.tool !== null : event.tool === null;
}
